// Engaged time on screen — see PRD §3.1.1. Wall-clock dwell overcounts (it keeps running
// while the tab is backgrounded, the window is unfocused, or the user is AFK). We track a
// per-screen accumulator that pauses/resumes on focus + visibility + idle, so the reported
// `active_ms` reflects real attention. `total_ms` (wall-clock) is kept for the ratio.
//
// A few global listeners, registered once. No per-frame work, no render cost.

import { config } from "./config"

const INTERACTION_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"]

let enterTs = 0          // wall-clock screen entry
let activeMs = 0         // accumulated focused+visible+non-idle time
let resumeTs = 0         // when the current active stretch began (0 = paused)
let idle = false
let idleTimer = null
let lastArm = 0
let started = false

const now = () => (typeof performance !== "undefined" ? performance.now() : new Date().getTime())

function isFocused() {
    const visible = typeof document === "undefined" || document.visibilityState !== "hidden"
    const focused = typeof document === "undefined" || !document.hasFocus || document.hasFocus()
    return visible && focused && !idle
}

function pause() {
    if (resumeTs) {
        activeMs += now() - resumeTs
        resumeTs = 0
    }
}

function resume() {
    if (!resumeTs && isFocused()) resumeTs = now()
}

function armIdleTimer() {
    if (idleTimer) clearTimeout(idleTimer)
    lastArm = now()
    idleTimer = setTimeout(() => {
        try { idle = true; pause() } catch { /* fail-silent */ }
    }, config.idleTimeoutMs)
}

// Any interaction (DOM event or an explicit track() call) clears the idle state and
// resumes the timer. Exported so the tracker can count programmatic events as activity.
// Registered on mousemove/scroll/etc, so it runs at very high frequency: re-arming the
// idle timer on every call would be needless clearTimeout/setTimeout churn on the main
// thread, so we throttle the re-arm to at most once/second (idle granularity is seconds).
export function markInteraction() {
    if (idle) {
        idle = false
        resume()
    }
    if (now() - lastArm < 1000) return
    armIdleTimer()
}

function onVisibility() {
    if (document.visibilityState === "hidden") pause()
    else resume()
}

export function startEngagement() {
    if (started || typeof window === "undefined") return
    started = true
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("blur", pause)
    window.addEventListener("focus", resume)
    INTERACTION_EVENTS.forEach(e =>
        window.addEventListener(e, markInteraction, { passive: true, capture: true })
    )
}

// Reset the accumulator for a freshly entered screen.
export function enterScreen() {
    enterTs = now()
    activeMs = 0
    resumeTs = isFocused() ? enterTs : 0
    idle = false
    armIdleTimer()
}

// Snapshot the leaving screen's engagement. Returns ms, rounded.
export function leaveScreen() {
    pause()
    // Clear the idle timer so an orphan can't fire against the next screen's accumulator.
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null }
    return {
        active_ms: Math.round(activeMs),
        total_ms: Math.round(now() - enterTs),
    }
}
